import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { authApi } from "../services/api";
import Logo from "../components/Logo";
import Button from "../components/Button";
import Input from "../components/Input";
import LanguageSwitcher from "../components/LanguageSwitcher";

const T = { night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3", gold:"#C9922A" };
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Register() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName:"", lastName:"", phoneNumber:"", email:"", password:"", confirmPassword:"",
    countryCode:"CM",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const currentLanguage = i18n.language?.startsWith("en") ? "EN" : "FR";

  const handle = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const e = {};
    if (!form.firstName)    e.firstName    = t("register.field_required");
    if (!form.lastName)     e.lastName     = t("register.field_required");
    if (!form.phoneNumber)  e.phoneNumber  = t("register.field_required");
    if (!form.email)                        e.email = t("register.field_required");
    else if (!EMAIL_REGEX.test(form.email)) e.email = t("register.invalid_email");
    if (!form.password)     e.password     = t("register.password_min");
    else if (form.password.length < 8) e.password = t("register.password_min");
    if (form.password !== form.confirmPassword) e.confirmPassword = t("register.password_mismatch");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await authApi.register({
        firstName: form.firstName, lastName: form.lastName,
        phoneNumber: form.phoneNumber, email: form.email, password: form.password,
        countryCode: form.countryCode, language: currentLanguage,
      });
      toast.success(t("register.otp_toast"));
      navigate("/verify-otp", { state: { phoneNumber: form.phoneNumber, email: form.email } });
    } catch (err) {
      toast.error(err.response?.data?.message || t("register.error"));
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center",
      justifyContent:"center", background:"#F7F9FC",
      padding:"32px 24px", fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ width:"100%", maxWidth:"480px" }}>
        <div style={{ marginBottom:"32px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <Logo size="md" />
            <LanguageSwitcher />
          </div>
          <h1 style={{ fontSize:"26px", fontWeight:800, color:T.night, marginTop:"24px", marginBottom:"4px" }}>
            {t("register.title")}</h1>
          <p style={{ color:"#64748B", fontSize:"13px", marginBottom:"20px" }}>
            {t("register.subtitle")}</p>
        </div>

        <form onSubmit={submit}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <Input label={t("register.firstname")} name="firstName" placeholder="Mathurin"
              value={form.firstName} onChange={handle} error={errors.firstName} required />
            <Input label={t("register.lastname")} name="lastName" placeholder="Nguemo"
              value={form.lastName} onChange={handle} error={errors.lastName} required />
          </div>
          <Input label={t("register.phone")} name="phoneNumber" type="tel"
            placeholder="+237 6 XX XX XX XX" value={form.phoneNumber}
            onChange={handle} error={errors.phoneNumber} required
            note={t("register.otp_info")} />
          <Input label={t("register.email")} name="email" type="email"
            placeholder={t("register.email_placeholder")} value={form.email}
            onChange={handle} error={errors.email} required
            note={t("register.email_note")} />
          <div style={{ position:"relative" }}>
            <Input label={t("register.password")} name="password"
              type={showPwd ? "text" : "password"}
              placeholder={t("register.password_placeholder")}
              value={form.password}
              onChange={handle} error={errors.password} required />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              aria-label={showPwd ? t("register.hide_password") : t("register.show_password")}
              title={showPwd ? t("register.hide_password") : t("register.show_password")}
              style={{ position:"absolute", right:"14px", top:"38px",
                background:"none", border:"none", cursor:"pointer", fontSize:"16px",
                lineHeight:1 }}>
              {showPwd ? "🙈" : "👁️"}
            </button>
          </div>
          <div style={{ position:"relative" }}>
            <Input label={t("register.confirm_password")} name="confirmPassword"
              type={showConfirmPwd ? "text" : "password"}
              placeholder={t("register.confirm_password_placeholder")}
              value={form.confirmPassword}
              onChange={handle} error={errors.confirmPassword} required />
            <button type="button" onClick={() => setShowConfirmPwd(!showConfirmPwd)}
              aria-label={showConfirmPwd ? t("register.hide_password") : t("register.show_password")}
              title={showConfirmPwd ? t("register.hide_password") : t("register.show_password")}
              style={{ position:"absolute", right:"14px", top:"38px",
                background:"none", border:"none", cursor:"pointer", fontSize:"16px",
                lineHeight:1 }}>
              {showConfirmPwd ? "🙈" : "👁️"}
            </button>
          </div>
          <Button type="submit" variant="cyan" full loading={loading}>{t("register.next")}</Button>
          <p style={{ textAlign:"center", marginTop:"16px", fontSize:"13px", color:"#64748B" }}>
            {t("register.already")}{" "}
            <Link to="/login" style={{ color:T.cyan, fontWeight:600, textDecoration:"none" }}>
              {t("register.signin")}</Link></p>
        </form>
      </div>
    </div>
  );
}
